import React from "react"
import CreatePurchaseModal from "./create_purchase_modal_container"
import { fetchVirtualSuiteTypes, fetchCategories as apiFetchCategories } from "../../util/virtual_staging_api_util"
import { stripFileExtension } from "../../util/easy_format_utils"

export default class VirtualStagingOrderModal extends React.Component {
  constructor(props) {
    super(props)

    const account_credit =
      (this.props.project.client?.firm?.broker_charge && this.props.project.client?.account_credit) ||
      this.props.currentUser.roles?.broker
        ? null
        : this.props.project.client?.account_credit?.amount

    this.state = {
      step: 1,
      selectedPhotoIds: [],
      configs: {},
      paymentModalOpen: false,
      account_credit,
      suiteTypes: [],
      categoryOptions: {},
      submitErrors: [],
      incompletePhotoIds: [],
      showExitConfirm: false,
      submitted: false,
      photosLoading: true,
      photosError: '',
      editingPhotoIds: [],
      editorOpen: false,
      draft: {},
      draftError: '',
      draftAttempted: false,
      mixedSettings: false,
      optionsLoading: true,
      optionsError: '',
    }

    this.goToStep2 = this.goToStep2.bind(this)
    this.goToStep3 = this.goToStep3.bind(this)
    this.handleSubmit = this.handleSubmit.bind(this)
    this.togglePhoto = this.togglePhoto.bind(this)

    this.galleryScrollRef = React.createRef()
    this.configureScrollRef = React.createRef()
    this.summaryScrollRef = React.createRef()
  }

  componentDidMount() {
    // When nested inside DashboardVirtualStagingModal (identified by onBack),
    // that parent already owns the scroll lock for the whole flow — don't touch it here,
    // or unmounting this step (e.g. clicking "Back") would unlock scrolling under the parent modal.
    if (!this.props.onBack) document.body.style.overflow = "hidden"
    this.props.fetchVirtualStagingInfo(this.props.project.id).then(() => this.setState({ photosLoading: false }))
      .catch(() => this.setState({photosLoading: false, photosError: 'Photos could not be loaded. Please close and reopen this window to try again.'}))

    fetchVirtualSuiteTypes().then(types => Promise.all(types.map(type =>
      apiFetchCategories(type.id).then(cats => [type.id, Object.values(cats).sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))])
    )).then(entries => this.setState({suiteTypes: types, categoryOptions: Object.fromEntries(entries), optionsLoading: false})))
      .catch(() => this.setState({optionsLoading: false, optionsError: 'Edit options could not be loaded. Please close and reopen this window to try again.'}))
  }

  componentWillUnmount() {
    if (!this.props.onBack) document.body.style.overflow = "unset"
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.step !== this.state.step || prevState.editorOpen !== this.state.editorOpen) {
      const ref = this.state.step === 1 ? this.galleryScrollRef : this.state.step === 3 ? this.summaryScrollRef : this.configureScrollRef
      if (ref.current) ref.current.scrollTop = 0
    }
  }

  togglePhoto(photoId) {
    const { selectedPhotoIds } = this.state
    if (selectedPhotoIds.includes(photoId)) {
      this.setState({ selectedPhotoIds: selectedPhotoIds.filter(id => id !== photoId) })
    } else {
      this.setState({ selectedPhotoIds: [...selectedPhotoIds, photoId] })
    }
  }

  skipsPayment() {
    const { project, currentUser } = this.props
    const clientRole = project.client?.role?.name
    return (
      clientRole === "admin" ||
      clientRole === "broker" ||
      currentUser?.roles?.broker ||
      (clientRole === "sub_broker" && project.client?.firm?.broker_charge_virtual_staging)
    )
  }

  categoriesForType(suiteTypeId) {
    return this.state.categoryOptions[suiteTypeId] || []
  }

  goToStep2() {
    this.setState({ step: 2, editorOpen: false, editingPhotoIds: [], draftError: '' })
  }

  goToStep3() {
    if (!this.state.selectedPhotoIds.length || this.state.selectedPhotoIds.some(id => !this.categoryForConfig(this.state.configs[id]))) return
    this.setState({ step: 3 })
  }

  categoryForConfig(config) {
    if (!config?.suiteTypeId || !config?.categoryId) return null
    return this.categoriesForType(config.suiteTypeId).find(category => String(category.id) === String(config.categoryId)) || null
  }

  configKey(config = {}) {
    return JSON.stringify([String(config.suiteTypeId || ''), String(config.categoryId || ''), (config.currentNote || '').trim()])
  }

  openGroupEditor(ids) {
    const photoIds = ids.filter(id => this.state.selectedPhotoIds.includes(id))
    if (!photoIds.length) return
    const configs = photoIds.map(id => this.state.configs[id])
    const allSame = configs.every(config => this.configKey(config) === this.configKey(configs[0]))
    const mixedSettings = !allSame
    const defaultType = this.state.suiteTypes.find(type => type.name?.toLowerCase().includes('virtual staging'))
    const suiteTypeId = mixedSettings ? '' : (configs[0]?.suiteTypeId || defaultType?.id?.toString() || '')
    const categories = this.categoriesForType(suiteTypeId)
    const draft = allSame && configs[0] ? {...configs[0]} : {
      suiteTypeId, categoryId: categories.length === 1 ? categories[0].id : '',
      productId: categories.length === 1 ? categories[0].default_product_id : null, currentNote: ''
    }
    this.setState({step: 2, editingPhotoIds: photoIds, draft, mixedSettings, editorOpen: true, draftError: '', draftAttempted: false})
  }

  updateDraft(field, value) {
    this.setState(prev => {
      const draft = {...prev.draft, [field]: value}
      if (field === 'suiteTypeId') {
        const categories = this.categoriesForType(value)
        draft.categoryId = categories.length === 1 ? categories[0].id : ''
        draft.productId = categories.length === 1 ? categories[0].default_product_id : null
      } else if (field === 'categoryId') {
        draft.productId = this.categoryForConfig(draft)?.default_product_id || null
      }
      return {draft, draftError: ''}
    })
  }

  applyGroupSettings() {
    const {draft, editingPhotoIds} = this.state
    const category = this.categoryForConfig(draft)
    if (!category) {
      this.setState({draftAttempted: true, draftError: 'Complete the marked selection before applying these settings.'})
      return
    }
    this.setState(prev => {
      const configs = {...prev.configs}
      editingPhotoIds.forEach(id => {
        configs[id] = {...draft, productId: category.default_product_id || null, currentNote: (draft.currentNote || '').trim()}
      })
      return {configs, editingPhotoIds: [], editorOpen: false, draftError: '', incompletePhotoIds: []}
    })
  }

  toggleEditingPhoto(id) {
    this.setState(prev => ({editingPhotoIds: prev.editingPhotoIds.includes(id) ? prev.editingPhotoIds.filter(photoId => photoId !== id) : [...prev.editingPhotoIds, id]}))
  }

  orderGroups() {
    const groups = {}
    this.state.selectedPhotoIds.forEach(id => {
      const config = this.state.configs[id]
      if (!this.categoryForConfig(config)) return
      const key = this.configKey(config)
      if (!groups[key]) groups[key] = {config, photoIds: []}
      groups[key].photoIds.push(id)
    })
    return Object.values(groups)
  }

  handleSuiteTypeChange(photoId, suiteTypeId) {
    const cats = this.categoriesForType(suiteTypeId)
    const { configs } = this.state
    this.setState({
      configs: {
        ...configs,
        [photoId]: {
          ...configs[photoId],
          suiteTypeId,
          categoryId: cats.length === 1 ? cats[0].id : null,
          productId: cats.length === 1 ? (cats[0].default_product_id || null) : null,
        }
      }
    })
  }

  updateConfig(photoId, field, value) {
    const { configs } = this.state
    const updated = { ...configs[photoId], [field]: value }

    if (field === "categoryId") {
      const cats = this.categoriesForType(configs[photoId]?.suiteTypeId)
      const category = cats.find(c => c.id === parseInt(value))
      updated.productId = category?.default_product_id || null
    }

    this.setState({ configs: { ...configs, [photoId]: updated } })
  }

  totalPrice() {
    const { selectedPhotoIds, configs } = this.state
    let total = 0
    selectedPhotoIds.forEach(photoId => {
      const cfg = configs[photoId]
      if (!cfg || !cfg.suiteTypeId || !cfg.categoryId) return
      const cats = this.categoriesForType(cfg.suiteTypeId)
      const category = cats.find(c => c.id === parseInt(cfg.categoryId))
      total += parseFloat(category?.price) || 0
    })
    return total
  }

  handleSubmit() {
    if (!this.state.selectedPhotoIds.length || this.state.selectedPhotoIds.some(id => !this.categoryForConfig(this.state.configs[id]))) {
      this.setState({paymentModalOpen: false, step: 2, submitErrors: ['Finish configuring each photo before placing your order.']})
      return Promise.resolve()
    }
    const { selectedPhotoIds, configs, account_credit } = this.state
    const { project, currentUser } = this.props

    let price = this.totalPrice()
    let coupon_info = null
    let discount = 0

    if (account_credit && price > 0) {
      if (account_credit >= price) {
        discount = price
        price = 0
      } else {
        discount = account_credit
        price = price - account_credit
      }
    }

    if (discount > 0 && account_credit) {
      coupon_info = {
        coupon_id: project.client?.account_credit?.id,
        coupon_amount: parseInt(account_credit) - discount,
        coupon_discount_amount: discount,
      }
    }

    // Group photos by category — one purchase per category
    const byCategory = {}
    selectedPhotoIds.forEach(photoId => {
      const cfg = configs[photoId] || {}
      const key = cfg.categoryId || "null"
      if (!byCategory[key]) byCategory[key] = { categoryId: cfg.categoryId, photos: [] }
      const noteText = (cfg.currentNote || "").trim()
      byCategory[key].photos.push({ photoId, notes: noteText })
    })

    const virtual_staging_purchases_attributes = Object.values(byCategory).map(({ categoryId, photos }) => ({
      project_id: project.id,
      virtual_staging_category_id: categoryId,
      virtual_staging_photos_attributes: photos.map(({ photoId, notes }) => ({
        media_id: photoId,
        notes,
      })),
    }))

    const order_batch = {
      coupon_info,
      project_id: project.id,
      virtual_staging_purchases_attributes,
      broker_booked: currentUser.roles?.broker || false,
    }

    this.props.createVirtualStagingPurchase(order_batch).then(() => {
      this.props.onReloadProject()
      if (discount > 0 && account_credit) {
        this.props.fetchCurrentUser(this.props.currentUser.id)
      }
      this.setState({ submitted: true, paymentModalOpen: false })
    }).catch(errors => {
      this.setState({
        paymentModalOpen: false,
        submitErrors: Array.isArray(errors) ? errors : ["Something went wrong. Please try again."],
      })
    })
  }

  stepTitle() {
    const titles = {
      1: "Choose your photos",
      2: "Choose edits and instructions",
      3: "Review your order",
    }
    return titles[this.state.step] || ""
  }

  renderStep1() {
    const {photos} = this.props
    const {selectedPhotoIds, photosLoading} = this.state
    return <div className="vsOrderScreen">
      <p className="vsGroupIntro">Choose the photos you want edited. You can give several photos the same instructions in the next step.</p>
      {this.state.optionsLoading && <p className="vsGroupIntro" role="status">Loading edit options…</p>}
      {this.state.optionsError && <p className="vsGroupError" role="alert">{this.state.optionsError}</p>}
      {this.state.photosError && <p className="vsGroupError" role="alert">{this.state.photosError}</p>}
      {photosLoading && <div className="vsOrderGalleryEmpty">Loading photos…</div>}
      {!photosLoading && !this.state.photosError && photos.length === 0 && <div className="vsOrderGalleryEmpty">Your media is still in production. Please check back later.</div>}
      {!photosLoading && photos.length > 0 && <div className="scrollEdgeWrapper">
        <div className="vsOrderGalleryGrid" ref={this.galleryScrollRef}>
          {photos.map(photo => <button type="button" key={photo.id}
            className={'vsOrderPhotoCard ' + (selectedPhotoIds.includes(photo.id) ? 'vsOrderPhotoCardSelected' : '')}
            aria-label={'Select ' + stripFileExtension(photo.filename)} aria-pressed={selectedPhotoIds.includes(photo.id)} onClick={() => this.togglePhoto(photo.id)}>
            <img src={photo.url} alt={photo.filename} className="vsOrderPhotoThumb" />
            <span className={'vsGroupCheckbox ' + (selectedPhotoIds.includes(photo.id) ? 'vsGroupCheckboxChecked' : '')} aria-hidden="true">{selectedPhotoIds.includes(photo.id) ? '✓' : ''}</span>
            <span className="vsOrderPhotoName">{stripFileExtension(photo.filename)}</span>
          </button>)}
        </div>

      </div>}
      <div className="vsOrderFooter vsGroupFooter">
        {this.props.onBack && <button type="button" className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={this.props.onBack}>Back</button>}
        <span className="vsGroupSelectionCount" role="status">{selectedPhotoIds.length} photo{selectedPhotoIds.length === 1 ? '' : 's'} selected</span>
        <button type="button" className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn" disabled={!selectedPhotoIds.length || photosLoading || this.state.optionsLoading || !!this.state.optionsError || !!this.state.photosError} onClick={this.goToStep2}>Choose edits →</button>
      </div>
    </div>
  }

  renderGroupEditor() {
    const {draft, editingPhotoIds, suiteTypes, mixedSettings, draftError} = this.state
    const categories = this.categoriesForType(draft.suiteTypeId)
    const category = this.categoryForConfig(draft)
    const count = editingPhotoIds.length
    const serviceMissing = this.state.draftAttempted && !draft.suiteTypeId
    const requestMissing = this.state.draftAttempted && !!draft.suiteTypeId && !category
    const isStaging = suiteTypes.find(type => String(type.id) === String(draft.suiteTypeId))?.name?.trim().toLowerCase() === 'virtual staging'
    const requestLabel = isStaging ? 'Select room type' : 'Specific request'
    return <div className="vsOrderScreen">
      <div className="scrollEdgeWrapper">
        <div className="vsOrderList vsGroupEditor" ref={this.configureScrollRef}>
          <div className="vsGroupEditorHeading">
            <strong>{count === 1 ? 'Edit this photo' : 'Edit ' + count + ' selected photos'}</strong>
            <p>These settings apply only to {count === 1 ? 'this photo' : 'the photos shown below'}. Other photos stay unchanged.</p>
          </div>
          {mixedSettings && <p className="vsGroupNotice">These photos have different settings. Choose the settings and instructions you want to apply to this group.</p>}
          <div className="vsGroupFields">
            <label htmlFor="vs-group-service">Virtual service{serviceMissing && <span className="vsGroupRequiredError" aria-hidden="true"> *</span>}
              <select id="vs-group-service" aria-invalid={serviceMissing || undefined} className={'vsOrderSelect' + (serviceMissing ? ' vsOrderSelectError' : '')} value={draft.suiteTypeId || ''} onChange={e => this.updateDraft('suiteTypeId', e.target.value)}>
                <option value="" disabled>Choose a service</option>
                {suiteTypes.filter(type => this.categoriesForType(type.id).length > 0).map(type => {
                  const prices = this.categoriesForType(type.id).map(cat => parseFloat(cat.price) || 0)
                  const same = prices.every(price => price === prices[0])
                  return <option key={type.id} value={type.id}>{type.name} — {same ? '$' + prices[0] : 'from $' + Math.min(...prices)} / photo</option>
                })}
              </select>
            </label>
            {categories.length > 1 && <label htmlFor="vs-group-request">{requestLabel}{requestMissing && <span className="vsGroupRequiredError" aria-hidden="true"> *</span>}
              <select id="vs-group-request" aria-invalid={requestMissing || undefined} className={'vsOrderSelect' + (requestMissing ? ' vsOrderSelectError' : '')} value={draft.categoryId || ''} onChange={e => this.updateDraft('categoryId', e.target.value)}>
                <option value="" disabled>{isStaging ? 'Select room type' : 'Choose an option'}</option>
                {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.name} — {'$'}{parseFloat(cat.price) || 0} / photo</option>)}
              </select>
            </label>}
            {categories.length === 1 && <div className="vsGroupIncludedOption"><span>{requestLabel}</span><strong>{categories[0].name}</strong></div>}
          </div>
          <label className="vsGroupInstructions" htmlFor="vs-group-instructions">{count === 1 ? 'Instructions for this photo' : 'Shared instructions for these photos'} <span>(optional)</span>
            <textarea id="vs-group-instructions" className="vsOrderNotesInput" rows="4" placeholder="Tell us what you would like changed, what to keep, and any preferences." value={draft.currentNote || ''} onChange={e => this.updateDraft('currentNote', e.target.value)} />
          </label>
          <div className="vsGroupDraftPrice">{category ? '$' + (parseFloat(category.price) || 0).toFixed(2) + ' per photo × ' + count + ' = $' + ((parseFloat(category.price) || 0) * count).toFixed(2) : 'Choose a service and option to see the price.'}</div>
          {draftError && <p className="vsGroupError" role="alert">{draftError}</p>}
          <div className="vsGroupPreview">
            {editingPhotoIds.map(id => {
              const photo = this.props.photos.find(item => item.id === id)
              return photo && <figure key={id}><img src={photo.url} alt={photo.filename} className="vsOrderPhotoThumb" /><figcaption>{stripFileExtension(photo.filename)}</figcaption></figure>
            })}
          </div>
        </div>

      </div>
      <div className="vsOrderFooter vsGroupFooter">
        <button type="button" className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={() => this.setState({editorOpen: false, editingPhotoIds: [], draftError: ''})}>Cancel</button>
        <button type="button" className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn" onClick={() => this.applyGroupSettings()}>Apply to {count === 1 ? 'photo' : count + ' photos'}</button>
      </div>
    </div>
  }

  renderConfigurationPhoto(id) {
    const photo = this.props.photos.find(item => item.id === id)
    if (!photo) return null
    const config = this.state.configs[id]
    const category = this.categoryForConfig(config)
    const selected = this.state.editingPhotoIds.includes(id)
    return <article key={id} className={'vsGroupPhoto ' + (selected ? 'vsGroupPhotoSelected' : '')}>
      <button type="button" className="vsOrderPhotoCard" aria-label={'Select for editing ' + stripFileExtension(photo.filename)} aria-pressed={selected} onClick={() => this.toggleEditingPhoto(id)}>
        <img src={photo.url} alt={photo.filename} className="vsOrderPhotoThumb" />
        <span className={'vsGroupCheckbox ' + (selected ? 'vsGroupCheckboxChecked' : '')} aria-hidden="true">{selected ? '✓' : ''}</span>
        <span className="vsOrderPhotoName">{stripFileExtension(photo.filename)}</span>
      </button>
      <div className="vsGroupPhotoDetails">
        <span>{category ? category.name + ' · $' + (parseFloat(category.price) || 0).toFixed(2) : 'Not configured yet'}</span>
        <div className="vsGroupPhotoLinks">
          <button type="button" onClick={() => this.openGroupEditor([id])}>{category ? 'Edit this photo' : 'Configure this photo'}</button>
          <button type="button" onClick={() => this.setState(prev => ({selectedPhotoIds: prev.selectedPhotoIds.filter(photoId => photoId !== id), editingPhotoIds: prev.editingPhotoIds.filter(photoId => photoId !== id)}))}>Remove</button>
        </div>
      </div>
    </article>
  }

  renderStep2() {
    if (this.state.editorOpen) return this.renderGroupEditor()
    const {selectedPhotoIds, configs, editingPhotoIds} = this.state
    const remaining = selectedPhotoIds.filter(id => !this.categoryForConfig(configs[id]))
    const groups = this.orderGroups()
    return <div className="vsOrderScreen">
      <div className="vsGroupToolbar">
        <div><strong role="status">{selectedPhotoIds.length - remaining.length} of {selectedPhotoIds.length} photos configured</strong><p>Choose photos that need the same edits, then configure them together.</p></div>
        <button type="button" className="projectEditButton projectEditButtonPrimary" disabled={!editingPhotoIds.length} onClick={() => this.openGroupEditor(editingPhotoIds)}>Configure selected{editingPhotoIds.length ? ' (' + editingPhotoIds.length + ')' : ''}</button>
      </div>
      <div className="scrollEdgeWrapper">
        <div className="vsOrderList vsGroupWorkspace" ref={this.configureScrollRef}>
          {remaining.length > 0 && <section className="vsGroupWorkspaceSection" aria-label="Remaining photos">
            <h3>Remaining photos ({remaining.length})</h3>
            <p className="vsGroupIntro">Select one photo or a group, then click Configure selected.</p>
            <div className="vsGroupWorkspaceGrid">{remaining.map(id => this.renderConfigurationPhoto(id))}</div>
          </section>}
          {groups.length > 0 && <section className="vsGroupWorkspaceSection" aria-label="Configured groups">
            <h3>Configured groups ({groups.length})</h3>
            {groups.map(group => {
              const {config, photoIds} = group
              const category = this.categoryForConfig(config)
              const type = this.state.suiteTypes.find(item => String(item.id) === String(config.suiteTypeId))
              return <section key={this.configKey(config)} className="vsGroupWorkspaceCard">
                <div className="vsGroupReviewHeading"><div><strong>{type?.name}</strong><span>{category.name} · {photoIds.length} photo{photoIds.length === 1 ? '' : 's'}</span></div><strong>{'$'}{((parseFloat(category.price) || 0) * photoIds.length).toFixed(2)}</strong></div>
                <p className="vsGroupReviewNote">{config.currentNote || 'No additional instructions.'}</p>
                <div className="vsGroupWorkspacePreview" aria-label="Group photo preview">
                  {photoIds.slice(0, 3).map(id => {
                    const photo = this.props.photos.find(item => item.id === id)
                    return photo && <img key={id} src={photo.url} alt={photo.filename} className="vsOrderPhotoThumb" />
                  })}
                </div>
                <div className="vsGroupWorkspaceActions">
                  <button type="button" className="vsGroupTextButton" onClick={() => this.openGroupEditor(photoIds)}>{photoIds.length === 1 ? 'Edit this request' : 'Edit this group'}</button>
                  <span>{photoIds.length} photo{photoIds.length === 1 ? '' : 's'} configured</span>
                </div>
                <details className="vsGroupWorkspacePhotos"><summary>View {photoIds.length} photo{photoIds.length === 1 ? '' : 's'}</summary><div className="vsGroupWorkspaceGrid">{photoIds.map(id => this.renderConfigurationPhoto(id))}</div></details>
              </section>
            })}
          </section>}
          {selectedPhotoIds.length > 0 && !remaining.length && <p className="vsGroupComplete" role="status">Every photo is configured. You can edit a group above or continue to review your order.</p>}
          {!selectedPhotoIds.length && <p>No photos selected. Choose photos to start your order.</p>}
        </div>

      </div>
      <div className="vsOrderFooter vsGroupFooter">
        <button type="button" className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={() => this.setState({step: 1, editingPhotoIds: []})}>Choose photos</button>
        <span className="vsGroupTotal">Total: {'$'}{this.totalPrice().toFixed(2)}</span>
        <button type="button" className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn" disabled={!selectedPhotoIds.length || !!remaining.length} onClick={this.goToStep3}>Review order →</button>
      </div>
    </div>
  }

  renderStep3() {
    return <div className="vsOrderScreen">
      <p className="vsGroupIntro">Check the photos, edits, and instructions below before continuing.</p>
      <div className="scrollEdgeWrapper">
        <div className="vsOrderSummaryList vsGroupReview" ref={this.summaryScrollRef}>
          {this.orderGroups().map(group => {
            const {config, photoIds} = group
            const category = this.categoryForConfig(config)
            const type = this.state.suiteTypes.find(item => String(item.id) === String(config.suiteTypeId))
            return <section key={this.configKey(config)} className="vsGroupReviewSection">
              <div className="vsGroupReviewHeading"><div><strong>{type?.name}</strong><span>{category.name} · {photoIds.length} photo{photoIds.length === 1 ? '' : 's'} · {'$'}{(parseFloat(category.price) || 0).toFixed(2)} each</span></div><strong>{'$'}{((parseFloat(category.price) || 0) * photoIds.length).toFixed(2)}</strong></div>
              <p className="vsGroupReviewNote">{config.currentNote || 'No additional instructions.'}</p>
              <button type="button" className="vsGroupTextButton" onClick={() => this.openGroupEditor(photoIds)}>{photoIds.length === 1 ? 'Edit this request' : 'Edit this group'}</button>
              <div className="vsGroupPreview">
                {photoIds.map(id => {
                  const photo = this.props.photos.find(item => item.id === id)
                  return photo && <button type="button" key={id} aria-label={'Edit photo ' + stripFileExtension(photo.filename)} onClick={() => this.openGroupEditor([id])}><img src={photo.url} alt={photo.filename} className="vsOrderPhotoThumb" /><span>{stripFileExtension(photo.filename)}</span></button>
                })}
              </div>
            </section>
          })}
        </div>

      </div>
      <div className="vsOrderFooter vsGroupFooter">
        <button type="button" className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={() => this.setState({step: 2, editingPhotoIds: [], editorOpen: false})}>Back</button>
        <span className="vsGroupTotal">Total: {'$'}{this.totalPrice().toFixed(2)}</span>
        <button type="button" className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn" onClick={() => this.setState({paymentModalOpen: true})}>{this.skipsPayment() ? 'Submit Order' : 'Checkout →'}</button>
      </div>
    </div>
  }

  render() {
    const { step, paymentModalOpen, showExitConfirm, submitted } = this.state
    const { project, onClose } = this.props

    if (submitted) {
      return (
        <div className="modal-background vsOrderModalBg">
          <div className="orderpreviewModal vsOrderModal vsCongratsModal" onClick={e => e.stopPropagation()}>
            <div className="orderPreviewModalComponent congratsModalComponent">
              <div className="congratsModalHeading">Congrats your virtual staging has been submitted!</div>
              <div className="congratsButtons">
                <button className="congratsViewListingButton" onClick={onClose}>Close</button>
                <button className="congratsViewDashboardButton" onClick={() => { onClose(); this.props.history?.push(`/projects_index/${project.slug}`) }}>View Project</button>
              </div>
            </div>
          </div>
        </div>
      )
    }

    if (paymentModalOpen) {
      return (
        <CreatePurchaseModal
          submitOrder={this.handleSubmit}
          project={project}
          changeParentState={s => this.setState(s)}
          total={this.totalPrice()}
        />
      )
    }

    return (
      <div className="modal-background vsOrderModalBg">
        <div className="orderpreviewModal vsOrderModal" onClick={e => e.stopPropagation()}>
          <div className="orderPreviewModalComponent">
            {showExitConfirm ? (
              <div key="exit" className="vsExitConfirm">
                <div className="vsExitConfirmText">Are you sure you want to exit? Progress will not be saved.</div>
                <div className="vsExitConfirmButtons">
                  <button className="projectEditButton projectEditButtonLight vsOrderFooterBtn" onClick={onClose}>Yes</button>
                  <button className="projectEditButton projectEditButtonPrimary vsOrderFooterBtn" onClick={() => this.setState({ showExitConfirm: false })}>No</button>
                </div>
              </div>
            ) : (
              <div key="content" className="vsOrderStepContent">
                <div className="contractorModalHeading">
                  <div className="contractorModalDescription">{this.stepTitle()}</div>
                  <button type="button" aria-label="Close virtual order" className="closeIcon vsGroupClose" onClick={() => this.setState({ showExitConfirm: true })}><span aria-hidden="true">×</span></button>
                </div>
                <ol className="vsGroupSteps" aria-label="Order steps">
                  {['Photos', 'Edits', 'Review'].map((label, index) => <li key={label} aria-current={step === index + 1 ? 'step' : undefined} className={step === index + 1 ? 'vsGroupStepActive' : ''}><span>{index + 1}</span>{label}</li>)}
                </ol>
                {step === 1 && this.renderStep1()}
                {step === 2 && this.renderStep2()}
                {step === 3 && this.renderStep3()}
                {this.state.submitErrors.length > 0 && (
                  <ul className="virtualStagingErrors errors mt-2 mb-0">
                    {this.state.submitErrors.map((err, i) => <li key={i}>{err}</li>)}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }
}
